import { dateTimeSchema } from "@clipboard-health/contract-core";
import { z } from "zod";

/**
 * Local mirror of the roster-window-settings contract schemas, augmented with
 * `allowShiftCancellation` and a partial PATCH body.
 *
 * TODO(XL-209): once the backend contract release adds `allowShiftCancellation`
 * and the partial PATCH body, delete this module and go back to
 * `rosterWindowSettingsContract.{getSettings,updateSettings}` — then bump
 * `@clipboard-health/contract-backend-main` via `npm run update:contract-packages`.
 * The window-rule shapes intentionally match the contract so only the new field
 * diverges while the dependency is pinned.
 */

const ROSTER_WINDOW_SETTING_TYPE = "roster-window-setting" as const;

const ROSTER_WINDOW_RULE_KEYS = [
  "longLeadThresholdDays",
  "longLeadOpenDaysBeforeStart",
  "shortLeadOpenDaysAfterPosting",
  "urgentThresholdDays",
] as const;

const rosterWindowRuleAttributesSchema = z.object({
  longLeadThresholdDays: z.number().int().nonnegative(),
  longLeadOpenDaysBeforeStart: z.number().int().nonnegative(),
  shortLeadOpenDaysAfterPosting: z.number().int().nonnegative(),
  urgentThresholdDays: z.number().int().nonnegative(),
});

export const rosterWindowSettingsResponseSchema = z.object({
  data: z.object({
    type: z.literal(ROSTER_WINDOW_SETTING_TYPE),
    id: z.string(),
    attributes: rosterWindowRuleAttributesSchema.extend({
      updatedAt: dateTimeSchema().nullable(),
      // Nullish for resilience if the backend release hasn't shipped the field
      // yet; call sites default an absent value to `true`.
      allowShiftCancellation: z.boolean().nullish(),
    }),
  }),
});

export const rosterWindowSettingsUpdateBodySchema = z.object({
  data: z.object({
    type: z.literal(ROSTER_WINDOW_SETTING_TYPE),
    // Partial: the window rules save as a group; the cancellation toggle can be
    // flipped on its own (the backend enforces all-or-none on the rules).
    attributes: rosterWindowRuleAttributesSchema
      .partial()
      .extend({ allowShiftCancellation: z.boolean().optional() })
      // Enforce the all-or-none rule invariant at runtime too, matching the
      // backend contract — so a partial rule subset or an empty payload can't
      // slip through even if the compile-time union is bypassed.
      .refine(
        (attributes) => {
          const providedRuleCount = ROSTER_WINDOW_RULE_KEYS.filter(
            (key) => attributes[key] !== undefined
          ).length;
          const hasCancellationUpdate = attributes.allowShiftCancellation !== undefined;
          const rulesAllOrNone =
            providedRuleCount === 0 || providedRuleCount === ROSTER_WINDOW_RULE_KEYS.length;
          return rulesAllOrNone && (providedRuleCount > 0 || hasCancellationUpdate);
        },
        // i18n-ignore-next-line -- internal zod validation message; never rendered to end users
        { message: "Provide the full set of window rules, the cancellation toggle, or both." }
      ),
  }),
});
